import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  Users, Activity, ScanLine, Download, LogOut, Loader2, Search, Eye,
  LayoutDashboard, Table2, BarChart3, CalendarCheck, FileSpreadsheet,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "deelnemers", label: "Deelnemers", icon: Table2 },
  { id: "statistieken", label: "Statistieken", icon: BarChart3 },
  { id: "evaluaties", label: "Welzijnsevaluaties", icon: CalendarCheck },
];
const EVAL_STATUS = ["Nieuw", "Gebeld", "Afspraak ingepland", "Afspraak voltooid"];
const SCORE_HEX = (v) => (v >= 75 ? "#6B8E74" : v >= 60 ? "#E0B84B" : v >= 40 ? "#D98A4E" : "#C86B5E");

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", min_leeftijd: "", max_leeftijd: "", min_score: "", max_score: "", max_stress: "", max_hormonen: "", pms: false, peri: false, meno: false, interesse: false, bodyscan: false });

  const authGuard = useCallback((e) => {
    if (e.response?.status === 401) { localStorage.removeItem("wm_token"); navigate("/admin/login"); }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (e) { authGuard(e); }
  }, [authGuard]);

  const loadParticipants = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
    try { const { data } = await api.get("/admin/participants", { params }); setParticipants(data); } catch (e) { authGuard(e); }
  }, [filters, authGuard]);

  const loadEvaluations = useCallback(async () => {
    try { const { data } = await api.get("/admin/evaluations"); setEvaluations(data); } catch (e) { authGuard(e); }
  }, [authGuard]);

  useEffect(() => {
    if (!localStorage.getItem("wm_token")) { navigate("/admin/login"); return; }
    Promise.all([loadStats(), loadParticipants(), loadEvaluations()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/admin/participants/${id}`); setDetail(data); } catch (e) { authGuard(e); }
  };

  const updateEval = async (id, patch) => {
    setEvaluations((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    try { await api.patch(`/admin/participants/${id}/evaluation`, patch); } catch (e) { authGuard(e); }
  };

  const evalTimers = useRef({});
  const updateEvalText = (id, patch) => {
    setEvaluations((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    const key = `${id}-${Object.keys(patch)[0]}`;
    clearTimeout(evalTimers.current[key]);
    evalTimers.current[key] = setTimeout(() => {
      api.patch(`/admin/participants/${id}/evaluation`, patch).catch((e) => authGuard(e));
    }, 500);
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { authGuard(e); }
  };

  const logout = () => { localStorage.removeItem("wm_token"); navigate("/admin/login"); };

  if (loading && !stats) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF7F0]"><Loader2 className="animate-spin text-[#6B8E74]" size={32} /></div>;
  }

  const metrics = [
    { icon: Users, label: "Totaal ingevuld", value: stats?.total_participants ?? 0 },
    { icon: CalendarCheck, label: "Vandaag", value: stats?.today ?? 0 },
    { icon: CalendarCheck, label: "Deze week", value: stats?.week ?? 0 },
    { icon: CalendarCheck, label: "Deze maand", value: stats?.month ?? 0 },
    { icon: Activity, label: "Gem. welzijnsscore", value: `${stats?.average_score ?? 0}/100` },
    { icon: Activity, label: "Gem. stress", value: `${stats?.avg_stress ?? 0}/100` },
    { icon: Activity, label: "Gem. hormonaal", value: `${stats?.avg_hormonal ?? 0}/100` },
    { icon: Activity, label: "Gem. mentale helderheid", value: `${stats?.avg_mentale ?? 0}/100` },
    { icon: ScanLine, label: "Aanvragen evaluatie", value: stats?.bodyscan_requests ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-[#FBF7F0]">
      <header className="bg-white border-b border-[#EDE7DC] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <h1 className="text-xl text-[#3A4A40]">NUNNU <span className="text-[#8FA38C] text-sm">Backoffice</span></h1>
          <div className="flex items-center gap-2">
            <button onClick={() => download("/admin/export.xlsx", "nunnu_export.xlsx")} data-testid="export-xlsx-button" className="inline-flex items-center gap-2 bg-[#6B8E74] hover:bg-[#56745E] text-white text-sm px-4 py-2.5 rounded-full transition-colors">
              <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
            </button>
            <button onClick={() => download("/admin/export", "nunnu_export.csv")} data-testid="export-csv-button" className="inline-flex items-center gap-2 bg-[#C88A6A] hover:bg-[#b5785a] text-white text-sm px-4 py-2.5 rounded-full transition-colors">
              <Download size={16} /> <span className="hidden sm:inline">CSV</span>
            </button>
            <button onClick={logout} data-testid="logout-button" className="inline-flex items-center gap-2 text-[#5B6B61] hover:text-[#3A4A40] text-sm px-3 py-2.5 transition-colors"><LogOut size={16} /></button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-[#6B8E74] text-[#3A4A40]" : "border-transparent text-[#8FA38C] hover:text-[#5B6B61]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-6">
        {tab === "dashboard" && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="metrics">
            {metrics.map((m) => (
              <div key={m.label} className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgba(58,74,64,0.05)] border border-[#EDE7DC]">
                <span className="bg-[#F1EEE5] text-[#6B8E74] rounded-xl p-2.5 inline-flex"><m.icon size={20} /></span>
                <p className="text-2xl sm:text-3xl font-serif text-[#3A4A40] mt-3">{m.value}</p>
                <p className="text-sm text-[#5B6B61]">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "deelnemers" && (
          <>
            <div className="bg-white rounded-3xl p-5 shadow-[0_8px_30px_rgba(58,74,64,0.05)] border border-[#EDE7DC] space-y-4">
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8FA38C]" />
                <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="search-input" placeholder="Zoek op naam, e-mail, telefoon, gemeente..." className="wm-input pl-11" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <input type="number" placeholder="Min. leeftijd" value={filters.min_leeftijd} onChange={(e) => setFilters({ ...filters, min_leeftijd: e.target.value })} data-testid="filter-min-leeftijd" className="wm-input" />
                <input type="number" placeholder="Max. leeftijd" value={filters.max_leeftijd} onChange={(e) => setFilters({ ...filters, max_leeftijd: e.target.value })} data-testid="filter-max-leeftijd" className="wm-input" />
                <input type="number" placeholder="Min. score" value={filters.min_score} onChange={(e) => setFilters({ ...filters, min_score: e.target.value })} data-testid="filter-min-score" className="wm-input" />
                <input type="number" placeholder="Max. score" value={filters.max_score} onChange={(e) => setFilters({ ...filters, max_score: e.target.value })} data-testid="filter-max-score" className="wm-input" />
              </div>
              <div className="flex flex-wrap gap-3">
                {[["pms", "PMS"], ["peri", "Perimenopauze"], ["meno", "Menopauze"], ["interesse", "Interesse NUNNU"], ["bodyscan", "Evaluatie aangevraagd"]].map(([k, l]) => (
                  <label key={k} className="inline-flex items-center gap-2 text-sm text-[#5B6B61] cursor-pointer">
                    <input type="checkbox" checked={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.checked })} data-testid={`filter-${k}`} className="w-4 h-4 accent-[#6B8E74]" /> {l}
                  </label>
                ))}
                <button onClick={loadParticipants} data-testid="apply-filters-button" className="ml-auto bg-[#6B8E74] hover:bg-[#56745E] text-white rounded-full px-5 py-2 text-sm font-medium">Toepassen</button>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(58,74,64,0.05)] border border-[#EDE7DC] overflow-hidden">
              <div className="p-5 border-b border-[#EDE7DC]"><h2 className="text-lg text-[#3A4A40]">Deelnemers <span className="text-[#8FA38C] text-sm">({participants.length})</span></h2></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap" data-testid="participants-table">
                  <thead><tr className="text-left text-[#8FA38C] border-b border-[#EDE7DC]">
                    {["Voornaam", "Familienaam", "E-mail", "Telefoon", "Leeftijd", "Gemeente", "Datum", "Score", "Stress", "Ment. helderheid", "Prikkelbaar.", "Vermoeidheid", "Hormonen", "Interesse", "Evaluatie", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {participants.length === 0 && <tr><td colSpan="16" className="px-4 py-10 text-center text-[#8FA38C]">Geen deelnemers gevonden.</td></tr>}
                    {participants.map((p, i) => {
                      const sub = p.subscores || {};
                      const launch = p.market?.mr_launch;
                      const launchList = Array.isArray(launch) ? launch : launch ? [launch] : [];
                      const interest = launchList.some((x) => x === "Ja" || x === "Misschien");
                      return (
                        <tr key={i} className="border-b border-[#F3F0E8] hover:bg-[#FBF7F0]" data-testid={`participant-row-${i}`}>
                          <td className="px-3 py-3 text-[#3A4A40] font-medium">{p.voornaam}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{p.familienaam}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{p.email}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{p.telefoon}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{p.leeftijd}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{p.gemeente}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{(p.created_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-3 font-semibold" style={{ color: SCORE_HEX(p.total_score) }}>{p.total_score}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{sub.stress ?? "-"}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{sub.mentale_helderheid ?? "-"}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{sub.prikkelbaarheid ?? "-"}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{sub.vermoeidheid ?? "-"}</td>
                          <td className="px-3 py-3 text-[#5B6B61]">{sub.hormonen ?? "-"}</td>
                          <td className="px-3 py-3">{interest ? <span className="text-[#6B8E74] font-medium">Ja</span> : <span className="text-[#8FA38C]">Nee</span>}</td>
                          <td className="px-3 py-3">{p.bodyscan_requested ? <span className="text-[#C88A6A] font-medium">Ja</span> : <span className="text-[#8FA38C]">Nee</span>}</td>
                          <td className="px-3 py-3"><button onClick={() => openDetail(p.id)} data-testid={`participant-detail-${i}`} className="inline-flex items-center gap-1 text-[#6B8E74] hover:text-[#56745E] font-medium"><Eye size={16} /></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "statistieken" && stats && (
          <div className="space-y-6" data-testid="statistieken">
            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Leeftijdsverdeling">
                <BarChart data={stats.age_distribution}><XAxis dataKey="groep" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="aantal" fill="#6B8E74" radius={[4, 4, 0, 0]} /></BarChart>
              </ChartCard>
              <ChartCard title="Gemiddelde score per categorie">
                <BarChart data={Object.entries(stats.domains).map(([k, l]) => ({ cat: l, score: stats.average_per_domain[k] ?? 0 }))} layout="vertical" margin={{ left: 20 }}><XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="cat" width={130} tick={{ fontSize: 9 }} /><Tooltip /><Bar dataKey="score" fill="#C88A6A" radius={[0, 4, 4, 0]} /></BarChart>
              </ChartCard>
              <ChartCard title="Stress per leeftijd">
                <BarChart data={stats.stress_per_age}><XAxis dataKey="leeftijd" tick={{ fontSize: 11 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="score" fill="#D98A4E" radius={[4, 4, 0, 0]} /></BarChart>
              </ChartCard>
              <ChartCard title="Hormonale balans per leeftijd">
                <BarChart data={stats.hormonen_per_age}><XAxis dataKey="leeftijd" tick={{ fontSize: 11 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="score" fill="#E08B84" radius={[4, 4, 0, 0]} /></BarChart>
              </ChartCard>
              <ChartCard title="Meest voorkomende klachten (top 10)" wide>
                <BarChart data={stats.top_complaints} layout="vertical" margin={{ left: 20 }}><XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="klacht" width={220} tick={{ fontSize: 9 }} /><Tooltip /><Bar dataKey="aantal" fill="#6B8E74" radius={[0, 4, 4, 0]} /></BarChart>
              </ChartCard>
            </div>

            {/* Hormone + levensfase */}
            {stats.hormone && (
              <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Verdeling levensfase">
                  <PieChart><Pie data={stats.hormone.phase_distribution} dataKey="aantal" nameKey="fase" cx="50%" cy="50%" outerRadius={85} label={(e) => (e.aantal > 0 ? e.aantal : "")}>{stats.hormone.phase_distribution.map((_, i) => <Cell key={i} fill={["#6B8E74", "#E0B84B", "#D98A4E", "#C86B5E", "#8FA38C"][i % 5]} />)}</Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} /></PieChart>
                </ChartCard>
                <ChartCard title="Hormonale klachten vs. andere domeinen">
                  <BarChart data={stats.hormone.correlations}><XAxis dataKey="domain" tick={{ fontSize: 10 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} /><Bar dataKey="hoge_klachten" name="Veel klachten" fill="#C86B5E" radius={[4, 4, 0, 0]} /><Bar dataKey="lage_klachten" name="Weinig klachten" fill="#6B8E74" radius={[4, 4, 0, 0]} /></BarChart>
                </ChartCard>
              </div>
            )}

            {/* Market */}
            <div className="grid lg:grid-cols-2 gap-6">
              {stats.market?.map((q) => (
                <ChartCard key={q.id} title={q.question}>
                  <BarChart data={q.data} layout="vertical" margin={{ left: 10 }}><XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="optie" width={130} tick={{ fontSize: 9 }} /><Tooltip /><Bar dataKey="aantal" fill="#C88A6A" radius={[0, 4, 4, 0]} /></BarChart>
                </ChartCard>
              ))}
            </div>
          </div>
        )}

        {tab === "evaluaties" && (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(58,74,64,0.05)] border border-[#EDE7DC] overflow-hidden" data-testid="evaluaties-table">
            <div className="p-5 border-b border-[#EDE7DC]"><h2 className="text-lg text-[#3A4A40]">Aanvragen gratis welzijnsevaluatie <span className="text-[#8FA38C] text-sm">({evaluations.length})</span></h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead><tr className="text-left text-[#8FA38C] border-b border-[#EDE7DC]">{["Naam", "Telefoon", "E-mail", "Gemeente", "Voorkeursdag", "Voorkeursmoment", "Status"].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {evaluations.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-[#8FA38C]">Nog geen aanvragen.</td></tr>}
                  {evaluations.map((e, i) => (
                    <tr key={e.id} className="border-b border-[#F3F0E8]" data-testid={`eval-row-${i}`}>
                      <td className="px-3 py-3 text-[#3A4A40] font-medium">{e.voornaam} {e.familienaam}</td>
                      <td className="px-3 py-3 text-[#5B6B61]">{e.telefoon}</td>
                      <td className="px-3 py-3 text-[#5B6B61]">{e.email}</td>
                      <td className="px-3 py-3 text-[#5B6B61]">{e.gemeente}</td>
                      <td className="px-3 py-3"><input value={e.voorkeursdag || ""} onChange={(ev) => updateEvalText(e.id, { voorkeursdag: ev.target.value })} data-testid={`eval-dag-${i}`} className="wm-input py-1.5 text-sm w-28" placeholder="—" /></td>
                      <td className="px-3 py-3"><input value={e.voorkeursmoment || ""} onChange={(ev) => updateEvalText(e.id, { voorkeursmoment: ev.target.value })} data-testid={`eval-moment-${i}`} className="wm-input py-1.5 text-sm w-28" placeholder="—" /></td>
                      <td className="px-3 py-3"><select value={e.eval_status || "Nieuw"} onChange={(ev) => updateEval(e.id, { eval_status: ev.target.value })} data-testid={`eval-status-${i}`} className="wm-input py-1.5 text-sm">{EVAL_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} onOpenReport={() => navigate(`/admin/report/${detail.id}`)} />}
    </div>
  );
}

const ChartCard = ({ title, children, wide }) => (
  <div className={`bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgba(58,74,64,0.05)] border border-[#EDE7DC] ${wide ? "lg:col-span-2" : ""}`}>
    <h3 className="text-sm font-medium text-[#3A4A40] mb-4 leading-snug">{title}</h3>
    <ResponsiveContainer width="100%" height={wide ? 320 : 240}>{children}</ResponsiveContainer>
  </div>
);

const DetailModal = ({ detail, onClose, onOpenReport }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="participant-detail-modal">
    <div className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-2xl text-[#3A4A40]">{detail.voornaam} {detail.familienaam}</h3>
          <p className="text-sm text-[#8FA38C]">{(detail.created_at || "").slice(0, 10)} • Score {detail.total_score}/100 — {detail.category}</p>
        </div>
        <button onClick={onClose} data-testid="close-detail-modal" className="text-[#8FA38C] hover:text-[#3A4A40] text-2xl leading-none">×</button>
      </div>
      <button onClick={onOpenReport} data-testid="open-report-button" className="w-full mb-5 bg-[#6B8E74] hover:bg-[#56745E] text-white rounded-full px-6 py-3 text-sm font-medium inline-flex items-center justify-center gap-2"><Eye size={16} /> Open volledig welzijnsrapport</button>

      <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
        {[["E-mail", detail.email], ["Telefoon", detail.telefoon], ["Leeftijd", detail.leeftijd], ["Gemeente", detail.gemeente], ["Levensfase", detail.levensfase || "—"], ["Evaluatie", detail.bodyscan_requested ? "Aangevraagd" : "Nee"]].map(([l, v]) => (
          <div key={l} className="bg-[#F1EEE5] rounded-xl px-4 py-2.5"><p className="text-xs text-[#8FA38C]">{l}</p><p className="text-[#3A4A40] break-words">{v}</p></div>
        ))}
      </div>

      {detail.interpretation && <div className="bg-[#F1EEE5] rounded-2xl p-4 mb-5"><p className="text-sm font-semibold text-[#6B8E74] mb-1">AI-interpretatie</p><p className="text-sm text-[#5B6B61]">{detail.interpretation}</p></div>}
      {detail.tips?.length > 0 && <div className="mb-5"><p className="text-sm font-semibold text-[#3A4A40] mb-2">Persoonlijke adviezen</p><ul className="space-y-1.5">{detail.tips.map((t, i) => <li key={i} className="text-sm text-[#5B6B61] flex gap-2"><span className="text-[#6B8E74]">•</span> {t}</li>)}</ul></div>}
    </div>
  </div>
);
